import { mergeAttributes } from '@tiptap/core';
import { getMentionSearchText } from './mention-search-text.js';
export const MENTION_SEARCH_HIGHLIGHT_SPEC_KEY = 'mentionSearchHighlight';
export const getMentionSearchHighlightsFromDecorations = (decorations) => {
    const highlights = [];
    for (const decoration of decorations) {
        const spec = decoration.spec?.[MENTION_SEARCH_HIGHLIGHT_SPEC_KEY];
        if (!spec) {
            continue;
        }
        if (spec.textFrom >= spec.textTo) {
            continue;
        }
        highlights.push(spec);
    }
    return highlights;
};
const SEARCH_HIGHLIGHT_CLASSES = ['ProseMirror-search-match', 'ProseMirror-active-search-match'];
const clearSearchHighlightClasses = (dom) => {
    for (const className of SEARCH_HIGHLIGHT_CLASSES) {
        dom.classList.remove(className);
    }
};
const buildHighlightedChildren = (text, highlights) => {
    if (highlights.length === 0 || text.length === 0) {
        return [text];
    }
    const classes = Array.from({ length: text.length }, () => null);
    for (const highlight of highlights) {
        const from = Math.max(0, Math.min(highlight.textFrom, text.length));
        const to = Math.max(0, Math.min(highlight.textTo, text.length));
        const isActive = highlight.className.includes('active');
        for (let i = from; i < to; i++) {
            if (isActive || !classes[i]) {
                classes[i] = highlight.className;
            }
        }
    }
    const children = [];
    let cursor = 0;
    while (cursor < text.length) {
        const className = classes[cursor];
        let end = cursor + 1;
        while (end < text.length && classes[end] === className) {
            end += 1;
        }
        const slice = text.slice(cursor, end);
        if (className) {
            const mark = document.createElement('span');
            mark.className = className;
            mark.textContent = slice;
            children.push(mark);
        }
        else {
            children.push(slice);
        }
        cursor = end;
    }
    return children;
};
const renderMentionContent = (dom, node, decorations) => {
    const text = getMentionSearchText(node);
    const highlights = getMentionSearchHighlightsFromDecorations(decorations);
    clearSearchHighlightClasses(dom);
    dom.replaceChildren();
    if (highlights.length === 1
        && highlights[0].textFrom <= 0
        && highlights[0].textTo >= text.length) {
        dom.classList.add(highlights[0].className);
        dom.textContent = text;
        return;
    }
    if (highlights.length === 0) {
        dom.textContent = text;
        return;
    }
    for (const child of buildHighlightedChildren(text, highlights)) {
        dom.append(child);
    }
};
export const createMentionSearchHighlightNodeView = () => {
    return ({ node, HTMLAttributes, decorations, extension }) => {
        const dom = document.createElement('span');
        const attrs = mergeAttributes(extension.options?.HTMLAttributes ?? {}, HTMLAttributes);
        Object.entries(attrs).forEach(([key, value]) => {
            if (value == null || value === false) {
                return;
            }
            if (key === 'class') {
                dom.className = String(value);
                return;
            }
            dom.setAttribute(key, String(value));
        });
        renderMentionContent(dom, node, decorations);
        return {
            dom,
            update(updatedNode, updatedDecorations) {
                if (updatedNode.type.name !== node.type.name) {
                    return false;
                }
                if (updatedNode.attrs.label != null) {
                    dom.setAttribute('data-label', String(updatedNode.attrs.label));
                }
                if (updatedNode.attrs.id != null) {
                    dom.setAttribute('data-id', String(updatedNode.attrs.id));
                }
                renderMentionContent(dom, updatedNode, updatedDecorations);
                return true;
            },
            ignoreMutation: () => true,
            selectNode: () => {
                dom.classList.add('ProseMirror-selectednode');
            },
            deselectNode: () => {
                dom.classList.remove('ProseMirror-selectednode');
            },
        };
    };
};
//# sourceMappingURL=mention-search-highlight-node-view.js.map